import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SegmentOption } from "#shared/reviewee-filters/filterOptionsContract.ts";
import { ListBoxItem, ListBoxLabel } from "@/components/ui/list-box.tsx";
import { useFilterOptionsClient } from "../../api/filterOptionsQuery.ts";
import { reviewCyclesI18NextInstanceKey } from "../../i18n/i18next.ts";
import { PagedLookupPanel, type PagedLookupPanelLabels } from "./PagedLookupPanel.tsx";

const PAGE_SIZE = 20;

const PLACEHOLDER_SEGMENTS: SegmentOption[] = [
    { id: "skeleton-1", name: "Xxxxxxxx Xxxxx", memberCount: 0 },
    { id: "skeleton-2", name: "Xxxx Xxxxxxxxx", memberCount: 0 },
    { id: "skeleton-3", name: "Xxxxxxx Xxxxxxxx", memberCount: 0 },
    { id: "skeleton-4", name: "Xxxxx Xxxx", memberCount: 0 }
];

export interface SegmentFilterPanelProps {
    selectedIds: string[];
    onSelectionChange: (segmentIds: string[]) => void;
    onBack: () => void;
    maxSelections?: number;
}

function renderSegment(segment: SegmentOption, untitled: string) {
    const label = segment.name || untitled;

    return (
        <ListBoxItem id={segment.id} textValue={label}>
            <ListBoxLabel className="truncate">{label}</ListBoxLabel>
        </ListBoxItem>
    );
}

export function SegmentFilterPanel({ selectedIds, onSelectionChange, onBack, maxSelections }: SegmentFilterPanelProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("SegmentFilterPanel", { i18n: i18nextInstance });
    const client = useFilterOptionsClient();

    const fetchPage = useCallback(({ q, continuationToken }: { q: string; continuationToken: string | undefined }) => client.searchSegments.call({ q: q || undefined, continuationToken, pageSize: PAGE_SIZE }), [client]);

    const renderItem = useCallback((segment: SegmentOption) => renderSegment(segment, t("untitled")), [t]);
    const getLoadedMoreMessage = useCallback((count: number) => t("liveRegion.loadedMore", { count }), [t]);
    const getRefreshedMessage = useCallback((count: number) => t("liveRegion.results", { count }), [t]);

    const labels = useMemo<PagedLookupPanelLabels>(
        () => ({
            title: t("title"),
            back: t("back"),
            searchPlaceholder: t("searchPlaceholder"),
            loading: t("loading"),
            empty: t("empty"),
            noResults: t("noResults"),
            error: t("error"),
            staleError: t("staleError"),
            getLoadedMoreMessage,
            getRefreshedMessage
        }),
        [t, getLoadedMoreMessage, getRefreshedMessage]
    );

    const queryKey = useMemo(() => client.searchSegments.key(), [client]);

    return <PagedLookupPanel labels={labels} queryKey={queryKey} fetchPage={fetchPage} placeholder={PLACEHOLDER_SEGMENTS} selectedIds={selectedIds} onSelectionChange={onSelectionChange} onBack={onBack} renderItem={renderItem} dependencies={[t]} maxSelections={maxSelections} />;
}
